import { useState, useCallback } from "react";
import { ServiceStep, ServiceStatus, BaseServiceInterface } from "../types/unified-services";
import { logError } from "../errors/ServiceErrorHandler";

export const useUnifiedBaseService = (initialStep: ServiceStep = ServiceStep.GUIDE): BaseServiceInterface => {
  const [currentStep, setCurrentStep] = useState<ServiceStep>(initialStep);
  const [status, setStatus] = useState<ServiceStatus>(ServiceStatus.IDLE);
  const [error, setError] = useState<string | null>(null);
  const [canProceedToNextStep, setCanProceedToNextStep] = useState(true);

  const resetService = useCallback(() => {
    setCurrentStep(ServiceStep.GUIDE);
    setStatus(ServiceStatus.IDLE);
    setError(null);
    setCanProceedToNextStep(true);
  }, []);

  const handleStepChange = useCallback((step: ServiceStep) => {
    setCurrentStep(step);
    setError(null);
    setCanProceedToNextStep(false);
  }, []);

  const handleStatusChange = useCallback((newStatus: ServiceStatus) => {
    setStatus(newStatus);

    if (newStatus === ServiceStatus.LOADING || newStatus === ServiceStatus.SUCCESS) {
      setError(null);
    }
  }, []);

  const handleError = useCallback((errorMessage: string | null) => {
    setError(errorMessage);
    if (errorMessage) {
      setStatus(ServiceStatus.ERROR);
      setCanProceedToNextStep(false);
      logError(new Error(errorMessage), "BaseService");
    }
  }, []);

  const goToNextStep = useCallback(() => {
    const nextStep = Math.min(currentStep + 1, ServiceStep.RESULTS) as ServiceStep;
    handleStepChange(nextStep);
  }, [currentStep]);

  const goToPreviousStep = useCallback(() => {
    const previousStep = Math.max(currentStep - 1, ServiceStep.GUIDE) as ServiceStep;
    handleStepChange(previousStep);
  }, [currentStep]);

  return {
    currentStep,
    status,
    error,
    canProceedToNextStep,

    setCurrentStep: handleStepChange,
    setStatus: handleStatusChange,
    setError: handleError,
    setCanProceedToNextStep,
    resetService,
    goToNextStep,
    goToPreviousStep,
  };
};
